import React, { ChangeEvent } from 'react';
import { Field, FieldProps } from 'formik';
import classNames from 'classnames';

import RadioGroupRow, {
  RadioGroupFieldType,
} from 'components/UI/Form/Row/RadioGroupRow/RadioGroupRow';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { useField } from '../hooks/useField';

import styles from './FormikTextRow.module.scss';

type FormikRadioRowType = RadioGroupFieldType & {
  beforeOnChange?: (e: ChangeEvent) => void;
  afterOnChange?: (e: ChangeEvent) => void;
  afterOnBlur?: (e: ChangeEvent) => void;
  classes?: RadioGroupFieldType['classes'] & {
    errorMessage?: string;
    label?: string;
    row?: string;
  };
  label?: React.ReactNode;
};

export default function FormikRadioGroupRow({
  label,
  name,
  classes,
  beforeOnChange,
  ...props
}: FormikRadioRowType): JSX.Element {
  const { errorMessage, row, label: labelCss, ...allClasses } = classes || {};
  const [
    field,
    { touched, error: formikError, apiError },
    { setTouched },
  ] = useField(name);

  return (
    <div className={row}>
      {label && <label className={labelCss}>{label}</label>}
      <Field
        name={name}
        field={field}
        classes={allClasses}
        beforeOnChange={(e: ChangeEvent) => {
          beforeOnChange?.(e);

          // Fix formik bug, it defines the "touch" only when the blur event is trigger
          setTouched(true);
        }}
        hasError={formikError && touched ? formikError : apiError || undefined}
        {...props}
        component={RadioGroupComponent}
      />
      <ErrorMessage
        name={name}
        additionalClass={classNames(styles.errorMessage, errorMessage)}
      />
    </div>
  );
}

type RadioRowComponentProps = FormikRadioRowType & FieldProps;

const RadioGroupComponent = ({
  field,
  beforeOnChange,
  afterOnChange,
  afterOnBlur,
  classes,
  hasError,
  options,
}: Omit<RadioRowComponentProps, 'name'>) => {
  const { onChange, onBlur, ...formikField } = { ...field };

  return (
    <RadioGroupRow
      onChange={e => {
        beforeOnChange?.(e);
        onChange(e);
        afterOnChange?.(e);
      }}
      onBlur={e => {
        onBlur(e);
        afterOnBlur?.(e);
      }}
      {...formikField}
      classes={classes}
      hasError={hasError}
      options={options}
    />
  );
};
